import { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth-context';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Stethoscope, MessageCircle, Clock, Star, Activity } from 'lucide-react';

export default function DoctorDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [queue, setQueue] = useState([]);
  const [activeChats, setActiveChats] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get('/api/doctor/queue').catch(() => ({ data: { sessions: [] } })),
      api.get('/api/doctor/active').catch(() => ({ data: { sessions: [] } })),
    ]).then(([qRes, aRes]) => {
      setQueue(qRes.data.sessions || []);
      setActiveChats(aRes.data.sessions || []);
    });
  }, []);

  const toggleOnline = async (online) => {
    await api.post('/api/doctor/online', { online });
    setProfile(prev => ({ ...prev, is_online: online }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold">Welcome, {user?.name?.split(' ').pop()}</h1>
          <p className="text-muted-foreground mt-1">Doctor Dashboard</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm">Online</span>
          <Switch checked={profile?.is_online} onCheckedChange={toggleOnline} />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="rounded-2xl"><CardContent className="pt-5 pb-4">
          <Stethoscope className="h-5 w-5 text-amber-600 mb-2" />
          <p className="text-2xl font-semibold">{queue.length}</p>
          <p className="text-xs text-muted-foreground">Waiting</p>
        </CardContent></Card>
        <Card className="rounded-2xl"><CardContent className="pt-5 pb-4">
          <MessageCircle className="h-5 w-5 text-primary mb-2" />
          <p className="text-2xl font-semibold">{activeChats.length}</p>
          <p className="text-xs text-muted-foreground">Active Chats</p>
        </CardContent></Card>
        <Card className="rounded-2xl"><CardContent className="pt-5 pb-4">
          <Clock className="h-5 w-5 text-blue-600 mb-2" />
          <p className="text-2xl font-semibold">0</p>
          <p className="text-xs text-muted-foreground">Today's Sessions</p>
        </CardContent></Card>
        <Card className="rounded-2xl"><CardContent className="pt-5 pb-4">
          <Star className="h-5 w-5 text-amber-500 mb-2" />
          <p className="text-2xl font-semibold">-</p>
          <p className="text-xs text-muted-foreground">Avg Rating</p>
        </CardContent></Card>
      </div>

      {queue.length > 0 && (
        <Card className="rounded-2xl border-amber-200 bg-amber-50/50">
          <CardContent className="pt-5">
            <h3 className="font-heading text-lg font-semibold mb-3">Patients Waiting</h3>
            <div className="space-y-2">
              {queue.map(s => (
                <div key={s.id} className="flex items-center justify-between p-3 rounded-xl border bg-white">
                  <div>
                    <p className="text-sm font-medium">{s.learner_name || 'Patient'}</p>
                    <p className="text-xs text-muted-foreground">{s.concern_category} concern</p>
                    {s.ai_intake_summary && <p className="text-xs text-primary mt-1 line-clamp-1">{s.ai_intake_summary.slice(0, 100)}...</p>}
                  </div>
                  <Button size="sm" onClick={async () => {
                    try { await api.post(`/api/doctor/pickup/${s.id}`); window.location.reload(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
                  }}>Pick Up</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {activeChats.length > 0 && (
        <Card className="rounded-2xl">
          <CardContent className="pt-5">
            <h3 className="font-heading text-lg font-semibold mb-3">Active Consultations</h3>
            <div className="space-y-2">
              {activeChats.map(s => (
                <div key={s.id} className="flex items-center justify-between p-3 rounded-xl border">
                  <div>
                    <p className="text-sm font-medium">{s.learner_name || 'Patient'}</p>
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">Active</Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => window.location.href = `/doctor/chat/${s.id}`}><MessageCircle className="h-3 w-3 mr-1" />Chat</Button>
                    <Button size="sm" variant="ghost" onClick={async () => {
                      if (window.confirm('Mark consultation as complete?')) {
                        await api.post(`/api/doctor/complete/${s.id}`);
                        window.location.reload();
                      }
                    }}>Complete</Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
